import { z } from 'zod';
import { SPECIES, type Species } from './species';

/**
 * Evcil Dostumun İhtiyacı (F7-39, ADR-147): **deterministik** kural motoru — yapay zekâ yok. Vitrin (ziyaretçinin
 * tarayıcısında), panel önizlemesi ve sunucu doğrulaması aynı işlevleri kullanır. Cevaplar sunucuya gönderilmez.
 *
 * Akış: tür → temel bilgiler (yaş, kilo aralığı, kısırlaştırma, gebelik/emzirme) → destek alanları → dallanan
 * sorular → sonuç. Acil durum seçeneği işaretlenirse ürün önerilmez; "önce veteriner hekime başvurun" gösterilir.
 * Önerilen ürün seçilen türe uygun değilse asla gösterilmez; hariç tutma kuralı her zaman önceliklidir.
 */

export const NF_LOCALES = ['tr', 'en', 'de', 'ar'] as const;
export type NfLocale = (typeof NF_LOCALES)[number];
/** Çok dilli metin: Türkçe zorunlu, diğerleri isteğe bağlı (yoksa Türkçe gösterilir). */
export const Localized = z.object({
  tr: z.string().trim().min(1).max(600),
  en: z.string().trim().max(600).optional(),
  de: z.string().trim().max(600).optional(),
  ar: z.string().trim().max(600).optional(),
});
export type Localized = z.infer<typeof Localized>;
export const textOf = (l: Localized | undefined, locale: string): string =>
  !l ? '' : (l as Record<string, string | undefined>)[locale] || l.tr;

export const AGE_BANDS = ['baby', 'young', 'adult', 'senior'] as const;
export type AgeBand = (typeof AGE_BANDS)[number];
export const WEIGHT_BANDS = ['under_2', '2_5', '5_10', '10_25', 'over_25'] as const;
export type WeightBand = (typeof WEIGHT_BANDS)[number];
export const NEUTERED = ['yes', 'no', 'unknown'] as const;
export const REPRODUCTIVE = ['none', 'pregnant', 'lactating', 'unknown'] as const;

/**
 * Türe göre sorulan temel bilgiler: kilo aralığı ve kısırlaştırma yalnızca kedi, köpek ve tavşanda; gebelik/emzirme
 * kısırlaştırılmamış (veya bilinmeyen) memelilerde. Sorulmayan bilgi boş kalır ve onu isteyen kural tutmaz.
 */
const WEIGHED: readonly Species[] = ['cat', 'dog', 'rabbit'];
const MAMMALS: readonly Species[] = ['cat', 'dog', 'rabbit', 'rodent'];
export function basicsFor(species: Species | null, neutered: NfState['neutered'] = null) {
  const s = species ?? 'exotic';
  return {
    weight: WEIGHED.includes(s),
    neutered: WEIGHED.includes(s),
    reproductive: MAMMALS.includes(s) && neutered !== 'yes',
  };
}

/** Temel bilgiler tamam mı (sorulan her alan cevaplı). */
export function basicsComplete(s: NfState): boolean {
  if (!s.species || !s.age) return false;
  const b = basicsFor(s.species, s.neutered);
  return (!b.weight || !!s.weight) && (!b.neutered || !!s.neutered) && (!b.reproductive || !!s.reproductive);
}

/** Tür veya kısırlaştırma değişince artık sorulmayan temel bilgiler temizlenir. */
export function normalizeBasics(s: NfState): NfState {
  const b = basicsFor(s.species, s.neutered);
  return {
    ...s,
    weight: b.weight ? s.weight : null,
    neutered: b.neutered ? s.neutered : null,
    reproductive: b.reproductive ? s.reproductive : null,
  };
}

const id = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, 'needs_finder_id_format');

/**
 * Koşul: verilen her alan eşleşmeli (VE); bir alan içindeki değerlerden biri yeterli (VEYA). Boş koşul her zaman
 * doğru. İç içe VEYA/DEĞİL yoktur: her koşul panelde düzenlenebilir kalır (gerekirse ikinci kural yazılır).
 */
export interface NfCondition {
  species?: Species[];
  ages?: AgeBand[];
  weights?: WeightBand[];
  neutered?: 'yes' | 'no';
  reproductive?: Array<'pregnant' | 'lactating'>;
  areas?: string[];
  answer?: { questionId: string; optionIds: string[] };
}
export const Condition: z.ZodType<NfCondition> = z
  .object({
    species: z.array(z.enum(SPECIES)).max(7).optional(),
    ages: z.array(z.enum(AGE_BANDS)).max(4).optional(),
    weights: z.array(z.enum(WEIGHT_BANDS)).max(5).optional(),
    neutered: z.enum(['yes', 'no']).optional(),
    reproductive: z
      .array(z.enum(['pregnant', 'lactating']))
      .max(2)
      .optional(),
    areas: z.array(id).max(30).optional(),
    answer: z.object({ questionId: id, optionIds: z.array(id).min(1).max(30) }).optional(),
  })
  .strict();

export const NeedsFinderDefinition = z
  .object({
    version: z.literal(1).default(1),
    /** Destek alanları (ürün kategorilerinden üretilir); yalnızca listelenen türlerde gösterilir. */
    areas: z
      .array(
        z.object({
          id,
          label: Localized,
          description: Localized.optional(),
          species: z.array(z.enum(SPECIES)).min(1),
          categoryIds: z.array(z.uuid()).max(20).default([]),
        }),
      )
      .max(30),
    /** Sorular tanım sırasıyla sorulur; alanı seçilmemiş veya koşulu tutmayan soru atlanır (dallanma). */
    questions: z
      .array(
        z.object({
          id,
          areaId: id.nullable().default(null),
          text: Localized,
          help: Localized.optional(),
          multiple: z.boolean().default(false),
          when: Condition.optional(),
          options: z
            .array(
              z.object({
                id,
                label: Localized,
                /** Seçilirse ürün önerilmez; veteriner hekime yönlendirilir. */
                emergency: z.boolean().default(false),
                /** Çoklu soruda tek başına seçilir ("Hiçbiri"): seçilince diğerleri, diğeri seçilince bu kalkar. */
                exclusive: z.boolean().default(false),
              }),
            )
            .min(2)
            .max(12),
        }),
      )
      .max(60),
    /** Öneri kuralları: koşul tutarsa ürün, onaylı gerekçesiyle önerilir; stokta yoksa sıradaki alternatif. */
    rules: z
      .array(
        z.object({
          id,
          when: Condition,
          productId: z.uuid(),
          reason: Localized,
          priority: z.number().int().min(0).max(100).default(50),
          alternatives: z.array(z.uuid()).max(5).default([]),
        }),
      )
      .max(300),
    /** Hariç tutma: koşul tutarsa bu ürünler hiçbir kuraldan önerilmez (ör. gebelikte uygun olmayan). */
    exclusions: z
      .array(
        z.object({
          id,
          when: Condition,
          productIds: z.array(z.uuid()).min(1).max(100),
          note: Localized.optional(),
        }),
      )
      .max(100),
    texts: z
      .object({
        intro: Localized.optional(),
        emergency: Localized.optional(),
      })
      .default({}),
    /** İsteğe bağlı ihtiyaç paketi indirimi: bağlı otomatik kampanya (F6-11); boş = kapalı (varsayılan). */
    bundlePromotionId: z.uuid().nullable().default(null),
  })
  .superRefine((d, c) => {
    const dup = (xs: string[], path: string) => {
      const seen = new Set<string>();
      xs.forEach((x, i) => {
        if (seen.has(x))
          c.addIssue({ code: 'custom', path: [path, i, 'id'], message: 'needs_finder_duplicate_id' });
        seen.add(x);
      });
    };
    dup(
      d.areas.map((a) => a.id),
      'areas',
    );
    dup(
      d.questions.map((q) => q.id),
      'questions',
    );
    dup(
      d.rules.map((r) => r.id),
      'rules',
    );
    dup(
      d.exclusions.map((r) => r.id),
      'exclusions',
    );
    const areas = new Set(d.areas.map((a) => a.id));
    const options = new Map(d.questions.map((q) => [q.id, new Set(q.options.map((o) => o.id))]));
    d.questions.forEach((q, i) => {
      if (q.areaId && !areas.has(q.areaId))
        c.addIssue({
          code: 'custom',
          path: ['questions', i, 'areaId'],
          message: 'needs_finder_unknown_area',
        });
      q.options.forEach((o, j) => {
        if (q.options.findIndex((x) => x.id === o.id) !== j)
          c.addIssue({
            code: 'custom',
            path: ['questions', i, 'options', j, 'id'],
            message: 'needs_finder_duplicate_id',
          });
      });
    });
    const check = (cond: NfCondition | undefined, path: Array<string | number>) => {
      if (!cond) return;
      for (const a of cond.areas ?? [])
        if (!areas.has(a)) c.addIssue({ code: 'custom', path, message: 'needs_finder_unknown_area' });
      if (cond.answer) {
        const opts = options.get(cond.answer.questionId);
        if (!opts || cond.answer.optionIds.some((o) => !opts.has(o)))
          c.addIssue({ code: 'custom', path, message: 'needs_finder_unknown_answer' });
      }
    };
    d.questions.forEach((q, i) => check(q.when, ['questions', i, 'when']));
    d.rules.forEach((r, i) => check(r.when, ['rules', i, 'when']));
    d.exclusions.forEach((r, i) => check(r.when, ['exclusions', i, 'when']));
  });
export type NeedsFinderDefinition = z.output<typeof NeedsFinderDefinition>;
export type NfQuestion = NeedsFinderDefinition['questions'][number];

/** Ziyaretçinin cevapları (yalnızca tarayıcıda). */
export interface NfState {
  species: Species | null;
  age: AgeBand | null;
  weight: WeightBand | null;
  neutered: (typeof NEUTERED)[number] | null;
  reproductive: (typeof REPRODUCTIVE)[number] | null;
  areas: string[];
  answers: Record<string, string[]>;
}
export const emptyState = (): NfState => ({
  species: null,
  age: null,
  weight: null,
  neutered: null,
  reproductive: null,
  areas: [],
  answers: {},
});

/** Koşul değerlendirmesi (tanımsız cevap koşulu tutturmaz). */
export function matches(c: NfCondition | undefined, s: NfState): boolean {
  if (!c) return true;
  if (c.species && (!s.species || !c.species.includes(s.species))) return false;
  if (c.ages && (!s.age || !c.ages.includes(s.age))) return false;
  if (c.weights && (!s.weight || !c.weights.includes(s.weight))) return false;
  if (c.neutered && s.neutered !== c.neutered) return false;
  if (c.reproductive && (!s.reproductive || !(c.reproductive as string[]).includes(s.reproductive)))
    return false;
  if (c.areas && !c.areas.some((a) => s.areas.includes(a))) return false;
  if (c.answer) {
    const got = s.answers[c.answer.questionId] ?? [];
    if (!c.answer.optionIds.some((o) => got.includes(o))) return false;
  }
  return true;
}

/** Seçilen türe uygun destek alanları. */
export const areasFor = (d: NeedsFinderDefinition, species: Species | null) =>
  species ? d.areas.filter((a) => a.species.includes(species)) : [];

/**
 * Sorulacak sorular (sırayla): genel sorular ve seçilen alanların soruları, koşulu tutanlar. Bir sorunun cevabı
 * sonraki soruların görünürlüğünü değiştirebilir (dallanma); görünmeyen sorunun cevabı yok sayılır.
 */
export function visibleQuestions(d: NeedsFinderDefinition, s: NfState): NfQuestion[] {
  const out: NfQuestion[] = [];
  const effective: NfState = { ...s, answers: {} };
  for (const q of d.questions) {
    if (q.areaId && !s.areas.includes(q.areaId)) continue;
    if (!matches(q.when, effective)) continue;
    out.push(q);
    if (s.answers[q.id]) effective.answers[q.id] = s.answers[q.id]!;
  }
  return out;
}

/** Bir seçeneğe tıklanınca sorunun yeni cevabı (tek seçimli: yalnızca o; çoklu: aç/kapat, "Hiçbiri" tek başına). */
export function applyAnswer(q: NfQuestion, prev: readonly string[], optionId: string): string[] {
  if (!q.options.some((o) => o.id === optionId)) return [...prev];
  if (!q.multiple) return [optionId];
  if (prev.includes(optionId)) return prev.filter((o) => o !== optionId);
  const exclusive = new Set(q.options.filter((o) => o.exclusive).map((o) => o.id));
  if (exclusive.has(optionId)) return [optionId];
  return [...prev.filter((o) => !exclusive.has(o)), optionId];
}

/** Görünür sorularda seçilmiş acil durum seçenekleri. */
export function emergencyOptions(d: NeedsFinderDefinition, s: NfState) {
  return visibleQuestions(d, s).flatMap((q) =>
    q.options
      .filter((o) => o.emergency && (s.answers[q.id] ?? []).includes(o.id))
      .map((o) => ({ question: q, option: o })),
  );
}

/** Vitrinin ürün bilgisi (sunucudan; yalnızca yayınlanmış tanımdaki ürünler). */
export interface NfProduct {
  id: string;
  species: Species[];
  inStock: boolean;
  /** Vitrinde satılabilir mi (yayında, ziyaretçiye açık). */
  available: boolean;
}

export interface NfRecommendation {
  productId: string;
  /** Eşleşen kuralların onaylı gerekçeleri (öncelik sırasıyla). */
  reasons: Localized[];
  ruleIds: string[];
  priority: number;
  /** Stokta olmayan önerinin yerine gelen alternatifse, yerine geçtiği ürün. */
  alternativeFor: string | null;
}

export interface NfResult {
  emergency: boolean;
  items: NfRecommendation[];
  /** Hariç tutulan veya türe uygun olmadığı için çıkarılan ürünler (panel önizlemesinde gösterilir). */
  removed: Array<{ productId: string; cause: 'excluded' | 'species' | 'unavailable' }>;
}

/**
 * Sonuç: acil durumda ürün yok. Aksi hâlde koşulu tutan kurallar önceliğe göre; aynı ürünün gerekçeleri birleşir.
 * Hariç tutma ve tür uygunluğu her öneride (alternatifler dahil) denetlenir. Stokta olmayan ürünün yerine ilk
 * uygun alternatif gelir; alternatif yoksa ürün "stokta yok" olarak listede kalır (vitrin işaretler).
 */
export function recommend(
  d: NeedsFinderDefinition,
  s: NfState,
  catalog: ReadonlyMap<string, NfProduct>,
): NfResult {
  const removed: NfResult['removed'] = [];
  if (!s.species) return { emergency: false, items: [], removed };
  if (emergencyOptions(d, s).length) return { emergency: true, items: [], removed };
  const effective: NfState = { ...s, answers: {} };
  for (const q of visibleQuestions(d, s)) if (s.answers[q.id]) effective.answers[q.id] = s.answers[q.id]!;
  const excluded = new Set(
    d.exclusions.filter((x) => matches(x.when, effective)).flatMap((x) => x.productIds),
  );
  const fits = (pid: string): 'ok' | 'excluded' | 'species' | 'unavailable' => {
    if (excluded.has(pid)) return 'excluded';
    const p = catalog.get(pid);
    if (!p || !p.available) return 'unavailable';
    if (!p.species.includes(s.species!)) return 'species';
    return 'ok';
  };
  const byProduct = new Map<string, NfRecommendation>();
  const rules = d.rules
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => matches(r.when, effective))
    .sort((a, b) => b.r.priority - a.r.priority || a.i - b.i);
  for (const { r } of rules) {
    let pid = r.productId;
    let alternativeFor: string | null = null;
    const status = fits(pid);
    // Hariç tutulan veya türe uygun olmayan ürünün kuralı hiç uygulanmaz (yerine alternatif de konmaz).
    if (status === 'excluded' || status === 'species') {
      removed.push({ productId: pid, cause: status });
      continue;
    }
    if (status === 'unavailable' || !catalog.get(pid)!.inStock) {
      const alt = r.alternatives.find((a) => fits(a) === 'ok' && catalog.get(a)!.inStock);
      if (alt) {
        alternativeFor = pid;
        pid = alt;
      } else if (status === 'unavailable') {
        removed.push({ productId: pid, cause: 'unavailable' });
        continue;
      }
    }
    const cur = byProduct.get(pid);
    if (cur) {
      if (!cur.ruleIds.includes(r.id)) {
        cur.reasons.push(r.reason);
        cur.ruleIds.push(r.id);
      }
    } else
      byProduct.set(pid, {
        productId: pid,
        reasons: [r.reason],
        ruleIds: [r.id],
        priority: r.priority,
        alternativeFor,
      });
  }
  const seen = new Set<string>();
  const dedupRemoved = removed.filter((x) => (seen.has(x.productId) ? false : (seen.add(x.productId), true)));
  return { emergency: false, items: [...byProduct.values()], removed: dedupRemoved };
}

/** Tanımdaki tüm metinler (sağlık beyanı denetimi için): alan, soru, seçenek, gerekçe, not ve sayfa metinleri. */
export function definitionTexts(d: NeedsFinderDefinition): Array<{ path: string; text: string }> {
  const out: Array<{ path: string; text: string }> = [];
  const push = (path: string, l: Localized | undefined) => {
    if (!l) return;
    for (const loc of NF_LOCALES) {
      const v = (l as Record<string, string | undefined>)[loc];
      if (v) out.push({ path: `${path}.${loc}`, text: v });
    }
  };
  d.areas.forEach((a) => {
    push(`areas.${a.id}.label`, a.label);
    push(`areas.${a.id}.description`, a.description);
  });
  d.questions.forEach((q) => {
    push(`questions.${q.id}.text`, q.text);
    push(`questions.${q.id}.help`, q.help);
    q.options.forEach((o) => push(`questions.${q.id}.options.${o.id}`, o.label));
  });
  d.rules.forEach((r) => push(`rules.${r.id}.reason`, r.reason));
  d.exclusions.forEach((x) => push(`exclusions.${x.id}.note`, x.note));
  push('texts.intro', d.texts.intro);
  push('texts.emergency', d.texts.emergency);
  return out;
}

/** Tanımda geçen ürünler (vitrin bu ürünlerin bilgisini ister). */
export function definitionProductIds(d: NeedsFinderDefinition): string[] {
  return [
    ...new Set([
      ...d.rules.flatMap((r) => [r.productId, ...r.alternatives]),
      ...d.exclusions.flatMap((x) => x.productIds),
    ]),
  ];
}
